import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Divider,
  Collapse,
  Avatar,
  Badge
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  FilterAlt as FilterIcon,
  AccessTime as TimeIcon,
  Code as CodeIcon,
  ExpandLess as ExpandLessIcon,
  Computer as ComputerIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import esdlService from '../../services/esdlService';
import { throttle } from 'lodash';

/**
 * Error logs component for ESDL Monitoring
 * Displays error logs with filtering options in a modern card layout
 */
const ErrorLogsTable = ({ region = '', hostname = '' }) => {
  // Add request tracking ref
  const requestInProgress = useRef(false);
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [errorLogs, setErrorLogs] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [filters, setFilters] = useState({
    region: region,
    hostname: hostname,
    hours: '24',
    errorType: 'wazuh'
  });
  
  // Options for filtering
  const hoursOptions = [
    { value: '1', label: 'Last 1 hour' },
    { value: '6', label: 'Last 6 hours' },
    { value: '12', label: 'Last 12 hours' },
    { value: '24', label: 'Last 24 hours' },
    { value: '48', label: 'Last 48 hours' },
    { value: '72', label: 'Last 72 hours' }
  ];
  
  const errorTypeOptions = [
    { value: 'wazuh', label: 'Wazuh Errors' },
    { value: 'filebeat', label: 'Filebeat Errors' }
  ];
  
  // Fetch error logs based on filters with throttling and caching
  const fetchErrorLogs = useCallback(
    throttle(async () => {
      // Check if a request is already in progress
      if (requestInProgress.current) {
        console.log('Error logs request already in progress');
        return;
      }
      
      requestInProgress.current = true;
      
      // Create cache key based on filters
      const cacheKey = `error-logs-${filters.region}-${filters.hostname}-${filters.hours}-${filters.errorType}`;
      
      // Check for cached data first
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          const cacheAge = Date.now() - timestamp;
          
          // Use cache if it's less than 2 minutes old
          if (cacheAge < 2 * 60 * 1000) {
            console.log('Using cached error logs data...');
            setErrorLogs(data || []);
            
            // Release request lock with a delay
            setTimeout(() => {
              requestInProgress.current = false;
            }, 1000);
            
            return;
          }
        } catch (error) {
          console.warn('Error parsing cached error logs:', error);
        }
      }
      
      try {
        setLoading(true);
        const response = await esdlService.getErrorLogs(filters);
        const logs = response.error_logs || [];
        
        setErrorLogs(logs);
        
        // Cache the response
        const cacheData = {
          data: logs,
          timestamp: Date.now()
        };
        
        try {
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (e) {
          console.warn('Failed to cache error logs:', e);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching error logs:', error);
        setErrorLogs([]);
        setLoading(false);
      } finally {
        // Release request lock with a delay
        setTimeout(() => {
          requestInProgress.current = false;
        }, 1000);
      }
    }, 1000, { leading: true, trailing: false }),
    [filters]
  );
  
  // Fetch error logs when filters change with debouncing
  useEffect(() => {
    // If we have preset hostname/region, only fetch once initially
    if ((hostname || region) && 
        filters.hostname === hostname && 
        filters.region === region && 
        errorLogs.length > 0) {
      return;
    }
    
    // Use a short timeout to debounce filter changes
    const timeoutId = setTimeout(() => {
      fetchErrorLogs();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [filters, hostname, region, fetchErrorLogs, errorLogs.length]);
  
  // Handle filter changes with throttling to prevent excessive API calls
  const handleFilterChange = useCallback(
    throttle((event) => {
      const { name, value } = event.target;
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }, 500),
    []
  );
  
  // Copy error message to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };
  
  // Toggle expansion of error log item
  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Get severity color based on error message
  const getSeverityColor = (errorText) => {
    const criticalKeywords = ['critical', 'failure', 'failed', 'error', 'cannot', 'denied'];
    const warningKeywords = ['warning', 'timeout', 'retry'];
    
    errorText = errorText.toLowerCase();
    
    if (criticalKeywords.some(keyword => errorText.includes(keyword))) {
      return 'error';
    }
    if (warningKeywords.some(keyword => errorText.includes(keyword))) {
      return 'warning';
    }
    
    return 'info';
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Filters */}
      <Paper 
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
          backdropFilter: 'blur(10px)',
          boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.06)}`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Stack direction="row" alignItems="center" mb={2} spacing={1}>
          <FilterIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="subtitle1" fontWeight="bold">
            Filter Error Logs
          </Typography>
        </Stack>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="time-range-label">Time Range</InputLabel>
              <Select
                labelId="time-range-label"
                id="time-range"
                name="hours"
                value={filters.hours}
                label="Time Range"
                onChange={handleFilterChange}
              >
                {hoursOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="error-type-label">Error Type</InputLabel>
              <Select
                labelId="error-type-label"
                id="error-type"
                name="errorType"
                value={filters.errorType}
                label="Error Type"
                onChange={handleFilterChange}
              >
                {errorTypeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <TextField
                label="Hostname"
                id="hostname"
                name="hostname"
                size="small"
                value={filters.hostname}
                onChange={handleFilterChange}
              />
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchErrorLogs}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none',
                boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.1)}`
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Error Logs Cards */}
      {errorLogs.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          p={4}
          sx={{
            background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.7)} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
            borderRadius: 2,
            border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
            backdropFilter: 'blur(10px)',
            minHeight: '200px'
          }}
        >
          <CheckIcon sx={{ fontSize: 60, color: theme.palette.success.main, mb: 2, opacity: 0.8 }} />
          <Typography variant="h6" color="text.primary" fontWeight="medium">
            No errors found
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400, mt: 1 }}>
            No error logs matching your filter criteria. Your systems appear to be running smoothly.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {errorLogs.map((log, logIndex) => (
            <Card
              key={`${log.hostname}-${logIndex}`}
              elevation={0}
              sx={{
                borderRadius: 2,
                background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.06)}`,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: `0 12px 20px ${alpha(theme.palette.common.black, 0.1)}`,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: 42,
                      height: 42,
                      boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                    }}
                  >
                    <ComputerIcon />
                  </Avatar>
                  
                  <Box flex={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {log.hostname}
                      </Typography>
                      <Chip
                        label={log.region}
                        size="small"
                        color="primary"
                        icon={<LocationIcon sx={{ fontSize: '0.8rem' }} />}
                        sx={{ height: 24, '& .MuiChip-label': { px: 1 } }}
                      />
                      <Badge 
                        badgeContent={log.errors.length} 
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        <ErrorIcon sx={{ color: theme.palette.error.main }} />
                      </Badge>
                    </Stack>
                    
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TimeIcon fontSize="small" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }} />
                      <Typography variant="caption" color="text.secondary">
                        {log.formatted_time}
                      </Typography>
                    </Stack>
                  </Box>
                  
                  <IconButton
                    onClick={() => toggleExpand(`${log.hostname}-${logIndex}`)}
                    sx={{ color: theme.palette.action.active }}
                  >
                    {expandedItems[`${log.hostname}-${logIndex}`] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Stack>
                
                <Collapse in={expandedItems[`${log.hostname}-${logIndex}`] || false}>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                    Error Messages:
                  </Typography>
                  
                  <Stack spacing={1.5} mb={1}>
                    {log.errors.map((error, errorIndex) => (
                      <Box
                        key={errorIndex}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette[getSeverityColor(error)].main, 0.08),
                          border: `1px solid ${alpha(theme.palette[getSeverityColor(error)].main, 0.2)}`,
                          position: 'relative'
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Box 
                            sx={{ 
                              color: theme.palette[getSeverityColor(error)].main,
                              pt: 0.2 
                            }}
                          >
                            <CodeIcon fontSize="small" />
                          </Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              pr: 4
                            }}
                          >
                            {error}
                          </Typography>
                          <Tooltip title="Copy to clipboard" placement="top">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(error)}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  color: theme.palette.primary.main
                                }
                              }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Collapse>
                
                {!expandedItems[`${log.hostname}-${logIndex}`] && log.errors.length > 0 && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    noWrap
                    sx={{ 
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      ml: 7,
                      fontStyle: 'italic' 
                    }}
                  >
                    {log.errors[0]}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ErrorLogsTable; 